import React, { useContext, useEffect, useRef, useState } from "react";
import { Box } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useParams } from "react-router-dom";
import { tesaBlue, tesaDarkRedError } from "../../../../../styles/colors";
import TestSeriesTable, { TestSeriesTableRef } from "./TestSeriesTable";
import { RegainConfig, TestSeriesContext, TestSeriesTabType } from "../../../../../contexts/TestSeriesContext";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import TestRequestApi from "../../../../../api/services/TestRequestApi";
import TestSeriesApi from "../../../../../api/services/TestSeriesApi";
import CustomAlert from "../../../../../components/Alert/CustomAlert";
import TestPlanPermissionContext from "../../../../../contexts/TestPlanPermissionContext";
import useEnv from "../../../../../hooks/env";
import RequestsPagePathParams from "../../../../../api/models/test-request/RequestsPagePathParams";
import TestPlanResponse from "../../../../../api/models/test-series/TestPlanResponse";
import ApiResponse from "../../../../../api/models/ApiResponse";
import TestSeriesDTO from "../../../../../api/models/TestSeriesDTO";
import ReusedTestSeriesApi from "../../../../../api/services/ReusedTestSeriesApi";
import { AnalyzeHistoricalFormType } from "../../AnalyzeHistoricalForm";

type TestMethodConfig = {
  id: number;
  version: number;
  template: boolean;
};

interface TestSeriesConfiguratorTabProps {
  loadDataInParent: () => void;
  onSevereError: () => void;
  testSeriesRef: (ref: TestSeriesTableRef) => void;
  testSeriesVersion: number;
  historicalSearchSettings: AnalyzeHistoricalFormType | null;
}

const ALERT_TEXT = `
  An appended hash # symbol indicates a multiple entry of a sample. For example, if the same sample 'Plastics' 
  is added twice, the second sample will be automatically named 'Plastics #2', then 'Plastics #3', and so on.
`;

function TestSeriesConfiguratorTab(props: TestSeriesConfiguratorTabProps) {
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const testSeriesApi = new TestSeriesApi(api);
  const reusedTestSeriesApi = new ReusedTestSeriesApi(api);
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const [loading, setLoading] = useState(true);
  const [initialTestSeriesString, setInitialTestSeriesString] = React.useState("");
  const {
    selectedTestSeriesId,
    selectedTestSeriesObject,
    updateSelectedTestSeriesId,
    updateSelectedTestSeriesObject,
    resetSelectedTestSeriesObject,
    updateSelectedTestSeriesHasTestCases,
    testMethod,
    regainConfig,
    updateOpenTabValue,
    updateTestMethod,
    updateRegainConfig,
    testSeriesIsReused,
  } = useContext(TestSeriesContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { updatePermissionState } = useContext(TestPlanPermissionContext);
  const { testPlans } = useContext(TestSeriesContext);
  const { loadDataInParent, testSeriesRef, testSeriesVersion, onSevereError, historicalSearchSettings } = props;
  const isMounted = useRef(false);

  const resetRegained = () => {
    setLoading(false);
    updateRegainConfig(null);
    updateTestMethod(undefined);
  };

  const resolveTestSeriesRegularCreator = (testMethodConfig: TestMethodConfig): ApiResponse<TestSeriesDTO> => {
    const defaultTestPlan = testPlans.find((testPlan: TestPlanResponse) => !testPlan.testPlanVersionName);
    if (!defaultTestPlan) {
      return Promise.reject(new Error("Could not retrieve default test plan."));
    }
    return testRequestApi.getTestSeriesByTestMethod(testMethodConfig, defaultTestPlan.id);
  };

  const resolveTestSeriesRegainedCreator = (
    testMethodConfig: TestMethodConfig,
    regainConfiguration: NonNullable<RegainConfig>,
  ): ApiResponse<TestSeriesDTO> => {
    const sourceTestPlan = testPlans.find((testPlan: TestPlanResponse) =>
      testPlan.testSeriesSearchResults.some((testSeries) => testSeries.id === regainConfiguration.originalTestSeriesId),
    );
    if (!sourceTestPlan) {
      return Promise.reject(new Error("Could not retrieve default test plan."));
    }

    return testRequestApi.createRegainedTestSeries(sourceTestPlan.id, regainConfiguration.originalTestSeriesId, {
      regainConfig: regainConfiguration.regainConfig,
      regainFirstSubstrateSection: regainConfiguration.regainFirstSubstrateSection,
      regainSecondSubstrateSection: regainConfiguration.regainSecondSubstrateSection,
      testMethodConfig: {
        template: testMethodConfig.template,
        testMethodId: testMethodConfig.id,
        testMethodVersion: testMethodConfig.version,
      },
    });
  };

  const createTestSeries = (
    methodConfiguration: TestMethodConfig,
    regainConfiguration: RegainConfig,
  ): ApiResponse<TestSeriesDTO> => {
    return regainConfiguration
      ? resolveTestSeriesRegainedCreator(methodConfiguration, regainConfiguration)
      : resolveTestSeriesRegularCreator(methodConfiguration);
  };

  const initializeFromSaved = () => {
    setLoading(true);
    const fetch = testSeriesIsReused
      ? () => reusedTestSeriesApi.fetchReusedTestSeries(testRequestId, selectedTestSeriesId as number)
      : () => testSeriesApi.fetchTestSeriesVersionById(selectedTestSeriesId as number, testSeriesVersion);

    fetch()
      .then(({ data }) => {
        updateSelectedTestSeriesObject(data);
        setInitialTestSeriesString(JSON.stringify(data));
      })
      .catch((error) => {
        handleHttpError(error);
        resetSelectedTestSeriesObject();
        updateSelectedTestSeriesHasTestCases(false);
        setInitialTestSeriesString("");
      })
      .finally(() => {
        resetRegained();
        setLoading(false);
      });
  };

  const fetchTestSeriesApiData = (): void => {
    if (selectedTestSeriesId) {
      // existing test series
      initializeFromSaved();
    } else if (testMethod) {
      // new test series
      const testMethodConfig = testMethod as {
        id: number;
        version: number;
        template: boolean;
      };
      setLoading(true);
      createTestSeries(testMethodConfig, regainConfig)
        .then((testSeriesResponse) => {
          return updatePermissionState().then(() => {
            updateSelectedTestSeriesObject(testSeriesResponse.data);
            updateSelectedTestSeriesHasTestCases(false);
            setInitialTestSeriesString(JSON.stringify(testSeriesResponse.data));
          });
        })
        .catch((error) => {
          handleHttpError(error);
          if (regainConfig) {
            // if the series was regained, load old test series
            updateSelectedTestSeriesId(selectedTestSeriesObject.id);
            updateOpenTabValue(TestSeriesTabType.TestTable);
          } else {
            resetSelectedTestSeriesObject();
            updateSelectedTestSeriesHasTestCases(false);
            setInitialTestSeriesString("");
            onSevereError();
          }
        })
        .finally(() => {
          resetRegained();
          setLoading(false);
        });
    }
  };

  const updateTestSeriesApiData = (testSeries: any): Promise<void> => {
    setLoading(true);
    return testRequestApi
      .updateTestSeries(testSeries)
      .then((response) => {
        setInitialTestSeriesString(JSON.stringify(response.data));
        updateSelectedTestSeriesObject(response.data);
        showSnackbar("Saved test series successfully", "success");
        setLoading(false);
        loadDataInParent();
      })
      .catch((error) => {
        setLoading(false);
        handleHttpError(error);
        throw error;
      });
  };

  const generateTestCaseTable = (testSeries: any): Promise<void> => {
    setLoading(true);
    return testRequestApi
      .generateTestCasesById(testSeries.id)
      .then((response) => {
        updateTestMethod(undefined);
        testSeries.testCases = response.data;
        setLoading(false);
        loadDataInParent();
      })
      .catch((error) => {
        setLoading(false);
        handleHttpError(error);
        throw error;
      });
  };

  useEffect(() => {
    if (isMounted.current) {
      initializeFromSaved();
    }
  }, [testSeriesVersion]);

  useEffect(() => {
    fetchTestSeriesApiData();
    isMounted.current = true;
  }, []);

  if (loading) {
    return <CircularProgress style={{ marginLeft: "50%", color: tesaBlue }} />;
  }

  return (
    <TestSeriesContext.Consumer>
      {(): JSX.Element => {
        return selectedTestSeriesObject ? (
          <>
            <CustomAlert text={ALERT_TEXT} severity="info" />
            <Box marginBottom="24px" />
            <TestSeriesTable
              ref={testSeriesRef}
              generateTestCaseTable={generateTestCaseTable}
              updateTestSeriesApiData={updateTestSeriesApiData}
              initialTestSeriesString={initialTestSeriesString}
              historicalSearchSettings={historicalSearchSettings}
            />
          </>
        ) : (
          <Box color={tesaDarkRedError}>Error loading Test Series.</Box>
        );
      }}
    </TestSeriesContext.Consumer>
  );
}

export default TestSeriesConfiguratorTab;
